import { TextSkeleton } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Stack, ThemeProvider } from "@mui/material";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { CancellationCreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CancellationCreditProgressBar";
import { useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";

import { PageWrapper } from "../../../components/PageWrapper";
import { CancelShiftFooter } from "./components/CancelShiftFooter";
import { CancelShiftHeader } from "./components/CancelShiftHeader";
import { ScoreBreakdownList } from "./components/ScoreBreakdownList";
import { ScoreDisplay } from "./components/ScoreDisplay";
import { useCancelShiftPageData } from "./hooks/useCancelShiftPageData";

function CancelShiftPageSkeleton(): JSX.Element {
  return (
    <Stack spacing={4} alignItems="center" sx={{ paddingY: 8, paddingX: 6 }}>
      <TextSkeleton variant="h2" width="60%" />
      <TextSkeleton variant="body2" width="80%" />
      <TextSkeleton variant="h1" width="30%" />
      <TextSkeleton variant="body2" width="100%" />
      <TextSkeleton variant="body2" width="100%" />
    </Stack>
  );
}

export function CancelShiftPage(): JSX.Element {
  const { shiftId } = useParams<{ shiftId: string }>();
  const history = useHistory();
  const currentDate = useCurrentDate(1000);

  const {
    isLoading,
    isError,
    currentScore,
    newScore,
    scoreChange,
    policyLabel,
    emergencyCreditStats,
    hasCancellationCredit,
    leadTimeHours,
  } = useCancelShiftPageData({
    shiftId,
    currentDate,
  });
  /*
   * This component is designed around emergency credits, and should only be invoked when the EC feature is enabled.
   * To avoid errors, it will gracefully degrade in the event that `emergencyCreditStats` is undefined. The resulting
   * behavior should be similar to the the standard bottom-sheet cancel flow, but has not been rigorously designed or tested.
   */

  const shouldShowWithCreditVariant = hasCancellationCredit && leadTimeHours < 24;

  const handleKeepShift = useCallback(() => {
    history.goBack();
  }, [history]);

  const handlePressAndHoldToCancel = useCallback(() => {
    // TODO: Implement press and hold functionality in separate ticket
    // For now, this button is shown but does not trigger cancellation
  }, []);

  const handlePreviewCredit = useCallback(() => {
    // MX-5838: Placeholder - does nothing for now
    // Future: Navigate to preview screen or trigger animation
  }, []);

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <CancelShiftHeader />
        {isLoading && <CancelShiftPageSkeleton />}
        {isError && (
          <Alert severity="error" sx={{ margin: 4 }}>
            Something went wrong while loading cancellation details. Please try again.
          </Alert>
        )}

        {!isLoading && !isError && isDefined(emergencyCreditStats) && (
          <Stack sx={{ flex: 1 }}>
            <ScoreDisplay score={newScore} />

            <Stack sx={{ paddingX: 6, paddingY: 4 }}>
              <CancellationCreditProgressBar
                showShiftsProgress={!shouldShowWithCreditVariant}
                cancellationCreditStats={emergencyCreditStats.cancellation}
              />
            </Stack>

            <ScoreBreakdownList
              currentScore={currentScore}
              scoreChange={scoreChange}
              policyLabel={policyLabel}
            />
          </Stack>
        )}
        <CancelShiftFooter
          variant={shouldShowWithCreditVariant ? "preview-credit" : "cancel-shift"}
          isLoading={isLoading}
          onKeepShift={handleKeepShift}
          onPressAndHoldToCancel={handlePressAndHoldToCancel}
          onPreviewCredit={handlePreviewCredit}
        />
      </PageWrapper>
    </ThemeProvider>
  );
}
